import { AfterViewInit, Component } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { saveAs as saveFile } from 'file-saver';
import { finalize } from 'rxjs/operators';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { Utils } from 'src/app/services/utils/utils.service';
import { environment } from '../../../environments/environment';
import { TransactionStatus } from '../../enums/transaction-status';
import { NewPlanOption } from '../../models/new-plan-option';
import { OwnerInfo } from '../../models/owner-info';
import { Transaction } from '../../models/transaction';
import { VehicleDetail } from '../../models/vehicle-detail';
import { DocumentDataService } from '../../services/document/document-data.service';
import { SAPDataService } from '../../services/sap/sap-data.service';
import { SharedStateService } from '../../services/shared-state/shared-state.service';
import { TransactionDataService } from '../../services/transaction/transaction-data.service';

@Component({
    selector: 'app-payment',
    templateUrl: './payment.component.' + environment.brand + '.html',
    styleUrls: ['./payment.component.' + environment.brand + '.css'],
    providers: [TransactionDataService, DocumentDataService, SAPDataService]
})
export class PaymentComponent implements AfterViewInit {
    payUReference: string;
    transactionId: string;
    transaction: Transaction;
    ownerInfo: OwnerInfo;
    vehicleDetail: VehicleDetail;

    statusMessage = '';
    infoLoaded = false;
    visibleElements: { [field: string]: boolean; } = {}; // key-value pair

    constructor(private sharedStateService: SharedStateService,
        private transactionDataService: TransactionDataService,
        private documentDataService: DocumentDataService,
        private sapDataService: SAPDataService,
        private route: ActivatedRoute,
        private utils: Utils,
        private analyticsService: AnalyticsService,
        private titleService: Title) {
        utils.log('PaymentComponent.constructor');
        if (environment.apiUrl.includes('localhost') || environment.apiUrl.includes('stg')) {
            this.visibleElements['CancelContract'] = true;
            this.visibleElements['RefundTransaction'] = true;
        }
    }

    ngAfterViewInit(): void {
        // get the payment reference from the url if it was set
        this.payUReference = this.route.snapshot.queryParamMap.get('PayUReference');
        if (this.payUReference) {
            this.utils.log('payUReference found');
            this.statusMessage = 'Please wait while we verify the status of your payment...';
            this.getTransaction();
        }

        // get the transaction id from the url if it was set
        this.transactionId = this.route.snapshot.queryParamMap.get('transactionId');
        if (this.transactionId) {
            this.utils.log('transactionId found');
            this.statusMessage = 'Loading details, please wait...';
            this.getTransaction();
        }
    }

    getTransaction(): void {
        this.utils.log('getTransaction');
        this.sharedStateService.toggleLoader(true);

        // decide which service call to use
        let getTransactionData;
        if (this.payUReference) {
            getTransactionData = this.transactionDataService.getTransactionStatus(this.payUReference);
        } else if (this.transactionId) {
            getTransactionData = this.transactionDataService.getTransaction(this.transactionId);
        }

        getTransactionData
            .pipe(finalize(() => {
                this.sharedStateService.toggleLoader(false);
                // get owner info
                if (this.sharedStateService.ownerInfo) {
                    this.utils.log('ownerInfo found');
                    this.setOwnerInfo(this.sharedStateService.ownerInfo);
                } else if (this.transaction) {
                    this.utils.log('ownerInfo not found, fetching it...');
                    this.getCustomerDetails();
                }
            }))
            .subscribe((transaction: Transaction) => {
                this.transaction = transaction;
                this.utils.log(JSON.stringify(this.transaction));

                if (this.transactionId) {
                    // user was directed here via link from session abandonment logic
                    return;
                }

                this.infoLoaded = true;

                if (transaction.transactionStatusId == TransactionStatus.Successful) {
                    this.visibleElements['PaymentSuccess'] = true;
                    this.statusMessage = 'Thank you. Payment successful.';
                    this.titleService.setTitle(`${this.titleService.getTitle()} Successful`);
                    this.analyticsService.addPageView('/payment/success');
                } else if (transaction.transactionStatusId == TransactionStatus.Failed) {
                    if (transaction.errorMessage?.toLowerCase().includes('cancelled')) {
                        this.visibleElements['UserCancelled'] = true;
                        this.statusMessage = 'You have cancelled your transaction.';
                        this.titleService.setTitle(`${this.titleService.getTitle()} Cancelled`);
                        this.analyticsService.addPageView('/payment/cancelled');
                    } else {
                        this.visibleElements['PaymentFailure'] = true;
                        this.statusMessage = 'We\'re sorry. Your payment was unsuccessful.';
                        this.titleService.setTitle(`${this.titleService.getTitle()} Unsuccessful`);
                        this.analyticsService.addPageView('/payment/unsuccessful');
                    }
                } else {
                    if (transaction.errorMessage?.includes('PayU Timeout')) {
                        this.visibleElements['PayUTimeout'] = true;
                        this.statusMessage = 'The transaction has timed out.';
                        this.titleService.setTitle(`${this.titleService.getTitle()} Timed Out`);
                        this.analyticsService.addPageView('/payment/timedout');
                    } else {
                        // default: could be API / PayU issue / still pending
                        this.visibleElements['PayUError'] = true;
                        this.statusMessage = 'Connectivity to PayU is temporarily down.';
                        this.titleService.setTitle(`${this.titleService.getTitle()} PayUError`);
                        this.analyticsService.addPageView('/payment/PayUError');
                    }
                }
            }, error => {
                this.utils.log('getTransaction', error);
                this.statusMessage = 'We could not verify the status of your payment at this time. ' +
                    'You can try again by refreshing the page or contact us for assistance. ' +
                    'Your payment reference is ' + this.payUReference;
                this.sharedStateService.showDialog({
                    isVisible: true,
                    header: 'Error verifying payment status',
                    message: 'We could not verify the status of your payment at this time.'
                });
            });
    }

    getCustomerDetails(): void {
        if (!this.transaction) {
            this.utils.log('Unable to get customer details');
            return;
        }

      this.sharedStateService.toggleLoader(true);
      this.sapDataService.getCustomerDetails(this.transaction.idNumber, '', this.transaction.vin)
            .pipe(finalize(() => { this.sharedStateService.toggleLoader(false) }))
            .subscribe(ownerInfo => {
                if (ownerInfo) {
                    this.setOwnerInfo(ownerInfo);
                } else {
                    this.sharedStateService.showDialog({
                        isVisible: true,
                        header: 'Owner Info not Found',
                        message: 'Please check your details or contact us for assistance.'
                    });
                    this.sharedStateService.enterCustomerDetailsErrorCount++;
                }
            }, error => {
                this.utils.log('getCustomerDetails', error);
                this.sharedStateService.showDialog({
                    isVisible: true,
                    header: 'Error getting customer details',
                    message: 'Please contact us for assistance.'
                });
            });
    }

    setOwnerInfo(ownerInfo: OwnerInfo): void {
        this.utils.log('setting owner info');
        this.ownerInfo = ownerInfo;

        if (!this.transaction) {
            return;
        }

      // security check
      if (
        this.transaction.idNumber &&
        ownerInfo?.idNumber &&
        this.transaction.idNumber !== ownerInfo.idNumber
      ) {
        this.transaction = undefined;
        this.sharedStateService.showDialog({
          isVisible: true,
          header: 'Transaction Owner Mismatch',
          message: 'Please contact us for assistance.'
        });

        this.ownerInfo = undefined;
        return;
      }

        // set the owner info
        this.ownerInfo.firstName = this.transaction.firstName;
        this.ownerInfo.lastName = this.transaction.lastName;
        this.ownerInfo.mobileNumber = this.transaction.mobileNumber;
        this.ownerInfo.email = this.transaction.emailAddress;
        this.vehicleDetail = this.ownerInfo.vehicleDetails.find(x => x.vin == this.transaction.vin);
        if (this.vehicleDetail) {
            this.vehicleDetail.currentOdometer = this.transaction.odometerReadingKm;
            this.vehicleDetail.vin = this.transaction.vin;
        }

        // set shared state
        this.sharedStateService.setOwnerInfo(this.ownerInfo);
        this.sharedStateService.selectedVehicleDetail = this.vehicleDetail;

        if (this.transaction.transactionStatusId != TransactionStatus.Successful) {
            // set data so user can retry payment            
            this.sharedStateService.newPlanOption = new NewPlanOption();
            this.sharedStateService.newPlanOption.salesDocType = this.transaction.salesDocType;
            this.sharedStateService.newPlanOption.brand = environment.brand;
            this.sharedStateService.newPlanOption.maxKilometers = this.transaction.newPlanDistanceKm;
            this.sharedStateService.newPlanOption.years = this.transaction.newPlanYears;
            this.sharedStateService.newPlanOption.planType = this.transaction.newPlanType;
            this.sharedStateService.newPlanOption.price = this.transaction.newPlanTotalIncludingVat;
            this.sharedStateService.newPlanOption.isCustomPlan = this.transaction.isCustomPlan;
            this.sharedStateService.newPlanOption.planStartKM = this.transaction.newPlanStartKms;
            this.sharedStateService.newPlanOption.maxKilometers = this.transaction.newPlanEndKms;
            this.sharedStateService.newPlanOption.planStartDate = this.transaction.newPlanStartDate;
            this.sharedStateService.newPlanOption.planEndDate = this.transaction.newPlanEndDate;
        }

        if (this.transactionId) {
            // session abandoned: redirect user to select-plan
            this.sharedStateService.showSelectPlan();
        }
    }

    onDownloadContractPdf(): void {
        this.sharedStateService.toggleLoader(true);
        this.documentDataService.getContractPdf(this.transaction)
            .pipe(finalize(() => { this.sharedStateService.toggleLoader(false); }))
            .subscribe(document => {
                const plan = environment.brand == 'vwsa' ? 'EasyDrive' : 'Freeway Plan';
                saveFile(document, plan + ' Contract ' + new Date().toISOString().split('T')[0] + '.pdf');
            }, error => {
                this.utils.log('downloadContractPdf', error);
                this.sharedStateService.showDialog({
                    isVisible: true,
                    header: 'Error downloading contract.',
                    message: 'Please contact us for assistance.'
                });
            });
    }

    onViewMyDetails(): void {
        if (this.ownerInfo) {
            // transaction successful: user redirected to their details with no previous values retained
            this.sharedStateService.ownerInfo.authVin = '';
            this.sharedStateService.selectedVehicleDetail = undefined;
            this.sharedStateService.showCustomerDetails();
        }
    }

    onShowViewQuote(): void {
        if (this.ownerInfo) {
            // transaction failed: user redirected to 'view quote' and retry purchase with previous values retained
            this.sharedStateService.showViewQuote();
        }
    }

    onShowSelectPlan(): void {
        if (this.ownerInfo) {
            this.sharedStateService.showSelectPlan();
        }
    }

    onCancelContract(): void {
        this.sharedStateService.toggleLoader(true);
        this.sapDataService.cancelContract(this.transaction.sapContractNumber)
            .pipe(finalize(() => { this.sharedStateService.toggleLoader(false); }))
            .subscribe(result => {
                this.sharedStateService.showDialog({
                    isVisible: true,
                    header: this.transaction.sapContractNumber,
                    message: result
                });
            }, error => {
                this.utils.log('onCancelContract', error);
                this.sharedStateService.showDialog({
                    isVisible: true,
                    header: 'Error Cancelling Contract',
                    message: error
                });
            });
    }
    
    onRefundTransaction(): void {
        this.sharedStateService.toggleLoader(true);
        this.transactionDataService.refundTransaction(this.transaction.payUReference)
            .pipe(finalize(() => { this.sharedStateService.toggleLoader(false); }))
            .subscribe(result => {
                this.sharedStateService.showDialog({
                    isVisible: true,
                    header: 'Transaction Refund',
                    message: result.result
                });
            }, error => {
                this.utils.log('onRefundTransaction', error);
                this.sharedStateService.showDialog({
                    isVisible: true,
                    header: 'Error Refunding Transaction',
                    message: error
                });
            });
    }
}
